/**
 * staffViewStyle.tsx
 * Shared look-and-feel for the "Staff this job" redesign (2026-07-14) so the in-app
 * screen matches the approved mockup `capacity-finalized.html` — softer, framed,
 * airier than the app's default 4px-radius / grey-hairline chrome. All colors are
 * derived from theme tokens (no hex literals — CODING-STANDARDS); the soft radii
 * and card shadow are presentation constants that mirror the mockup's `.card`.
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha, useTheme, type Theme } from '@mui/material/styles';

/** Mockup radii — soft rounded cards / cells / chips (vs the theme's boxy 4px). */
export const STAFF_CARD_RADIUS = 12;
export const STAFF_CELL_RADIUS = 9;
export const STAFF_CHIP_RADIUS = 8;

/** Mockup's soft two-layer card shadow. Shadow rgba, not a theme color. */
export const STAFF_SOFT_SHADOW =
  '0 1px 2px rgba(40, 50, 35, 0.06), 0 2px 10px rgba(40, 50, 35, 0.05)';

/** Soft sage hairline (mockup `--hair` #DCE3D2) — derived from the sage token. */
export const staffHair = (theme: Theme): string => alpha(theme.schedule.committed, 0.24);

/** Sage-tinted toolbar strip fill (mockup `.toolbar`). */
export const staffToolbarBg = (theme: Theme): string => alpha(theme.schedule.employeeAlloc, 0.08);

/**
 * Amber advisory pill used in the Tests table crew cell ("Needs mentor",
 * "N of M unstaffed") — clickable, with a hover deepen. Amber = the schedule
 * maintenance/attention token, never a hex literal.
 */
export const staffAmberPillSx = (theme: Theme) => ({
  display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
  border: `1px solid ${alpha(theme.schedule.maintenance, 0.5)}`,
  backgroundColor: alpha(theme.schedule.maintenance, 0.1), color: theme.schedule.maintenance,
  borderRadius: '999px', px: 0.9, py: 0.35,
  '&:hover': { backgroundColor: alpha(theme.schedule.maintenance, 0.2) },
});

/** The mockup `.card` — white paper, soft sage hairline, rounded, subtle shadow. */
export const staffCardSx = (theme: Theme) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${staffHair(theme)}`,
  borderRadius: `${STAFF_CARD_RADIUS}px`,
  boxShadow: STAFF_SOFT_SHADOW,
  overflow: 'hidden',
});

export interface StaffSectionLabelProps {
  /** The section number shown in the sage chip (mockup `.sec-label .n`). */
  n: number;
  title: string;
  hint?: string;
  /** Optional right-aligned content (e.g. an "N unstaffed" cue). */
  trailing?: React.ReactNode;
}

/**
 * The mockup's numbered section label: a small sage chip + bold sage title + a
 * muted hint. Replaces the old icon-and-text sub-headers so the two sections read
 * like the mockup's "1 · Tests on this job" / "2 · Team capacity".
 */
export const StaffSectionLabel: React.FC<StaffSectionLabelProps> = ({ n, title, hint, trailing }) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1, mt: 0.5 }}>
      <Box
        aria-hidden
        sx={{
          width: 21, height: 21, flexShrink: 0, borderRadius: '6px',
          backgroundColor: theme.schedule.employeeAlloc,
          color: theme.palette.getContrastText(theme.schedule.employeeAlloc),
          fontSize: '0.72rem', fontWeight: 800,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {n}
      </Box>
      <Typography sx={{ fontWeight: 800, fontSize: '0.92rem', letterSpacing: '-0.01em', color: theme.schedule.employeeAlloc }}>
        {title}
      </Typography>
      {hint && (
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 500 }}>
          {hint}
        </Typography>
      )}
      {trailing && <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>{trailing}</Box>}
    </Box>
  );
};
